import { AutoCompleteSelect } from '@components/core-ui/form-elements/AutocompleteSelect';
import { Input } from '@components/core-ui/form-elements/Input';
import { Select } from '@components/core-ui/form-elements/Select';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { TagMutedRounded } from '@components/core-ui/Tag/variations/TagMutedRounded';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminService } from 'app/API';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { layoutsKeys, TLayoutsListParams } from 'app/queries/layouts/layoutsKeys';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { createFetchOrganizationLayoutsQueryFn } from '../../../../api/fetchOrganizationLayouts';
import { LAYOUT_PAGE_SIZE, LAYOUTS_STALE_TIME_MS } from '../../../../helpers/layoutsListDefaults';
import {
  buildLayoutSortValue,
  DEFAULT_LAYOUT_SORT,
  LAYOUT_SORT_OPTIONS,
  parseLayoutSortValue,
} from '../../../../helpers/parseLayoutSortValue';
import { EmptyState } from '../../styles/EmptyState';
import { InlineControls } from '../../styles/InlineControls';
import { LayoutRow } from '../../styles/LayoutRow';
import { LayoutsList } from '../../styles/LayoutsList';
import { OrgCard } from '../../styles/OrgCard';
import { OrgHeader } from '../../styles/OrgHeader';
import { PaginationButton } from '../../styles/PaginationButton';
import { PaginationInfo } from '../../styles/PaginationInfo';
import { TProps } from './types/TProps';
import { LayoutDownloads } from './components/LayoutDownloads';

const SEARCH_DEBOUNCE_MS = 300;

export const OrganizationLayoutsCard = ({ organization, organizationOptions }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sortValue, setSortValue] = useState(buildLayoutSortValue(DEFAULT_LAYOUT_SORT));
  const [cloneTargets, setCloneTargets] = useState<Record<string, string>>({});
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;

  const listParams = useMemo<TLayoutsListParams>(
    () => ({ page, pageSize: LAYOUT_PAGE_SIZE, search: debouncedSearch, ...parseLayoutSortValue(sortValue) }),
    [debouncedSearch, page, sortValue],
  );

  const layoutsQuery = useQuery({
    queryKey: layoutsKeys.list(organization.id, listParams),
    queryFn: createFetchOrganizationLayoutsQueryFn(call),
    placeholderData: keepPreviousData,
    staleTime: LAYOUTS_STALE_TIME_MS,
    enabled: !isSearchSettling,
  });
  const layouts = layoutsQuery.data?.data ?? [];
  const count = layoutsQuery.data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil((count || layouts.length || 1) / LAYOUT_PAGE_SIZE));

  const cloneMutation = useMutation({
    mutationFn: ({ layoutId, targetOrganizationId }: { layoutId: string; targetOrganizationId: string }) =>
      call(() => AdminService.cloneLayoutToOrganization({ layoutId, requestBody: { targetOrganizationId } })),
    onSuccess: (_response, { targetOrganizationId }) => {
      toast.success('Layout cloned');
      void queryClient.invalidateQueries({ queryKey: layoutsKeys.listsByOrganization(organization.id) });
      void queryClient.invalidateQueries({ queryKey: layoutsKeys.listsByOrganization(targetOrganizationId) });
    },
    onError: () => {
      toast.error('Failed to clone layout. Please try again.');
    },
  });
  const cloningLayoutId = cloneMutation.isPending ? cloneMutation.variables?.layoutId ?? null : null;

  const handleClone = (layoutId: string) => {
    const targetOrganizationId = cloneTargets[layoutId];
    if (!targetOrganizationId) {
      toast.error('Select a target organization first');
      return;
    }
    cloneMutation.mutate({ layoutId, targetOrganizationId });
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleSortChange = (value: string) => {
    setSortValue(value);
    setPage(1);
  };

  const targetOptions = organizationOptions.filter((option) => option.value !== organization.id);
  const isRefreshing = layoutsQuery.isFetching || isSearchSettling;

  return (
    <OrgCard>
      <OrgHeader>
        <div className="org-name">
          <Link to={routes.adminOrganizationDetails.make(organization.id)}>{organization.name}</Link>
        </div>
        <div className="org-meta">{organization.slug}</div>
      </OrgHeader>

      <InlineControls>
        <Input
          name={`layout-search-${organization.id}`}
          label="Search layouts"
          value={search}
          onChange={(event) => handleSearchChange(event.target.value)}
          className="form-input w-64"
        />
        <Select
          name={`layout-sort-${organization.id}`}
          label="Sort by"
          value={sortValue}
          options={LAYOUT_SORT_OPTIONS}
          onChange={(_name, value) => handleSortChange(value)}
          className="w-52"
        />
        <PaginationButton disabled={page <= 1} onClick={() => setPage(Math.max(1, page - 1))}>
          Prev
        </PaginationButton>
        <PaginationInfo>
          Page {page} / {totalPages} ({count} layouts)
        </PaginationInfo>
        <PaginationButton disabled={page >= totalPages} onClick={() => setPage(Math.min(totalPages, page + 1))}>
          Next
        </PaginationButton>
        {isRefreshing ? <PaginationInfo>Loading layouts...</PaginationInfo> : null}
      </InlineControls>

      <LayoutsList>
        {layouts.length ? (
          layouts.map((layout) => (
            <LayoutRow key={layout.id}>
              <div className="layout-info">
                <div className="layout-name flex items-center gap-2">
                  <span>{layout.name}</span>
                  {layout.deleted ? <TagMutedRounded text="Deleted" /> : null}
                </div>
                <div className="layout-meta">
                  Template: {layout.templateName || '---'} | Created {getAmericanTime(layout.createdAt, false)}
                  {layout.updatedAt ? ` | Updated ${getAmericanTime(layout.updatedAt)}` : ''}
                </div>
                <div className="layout-meta">
                  Creator:{' '}
                  {layout.creatorId ? (
                    <Link
                      to={routes.adminUserDetails.make(layout.creatorId)}
                      className="text-[var(--color-accent)] underline"
                    >
                      {layout.creatorName || layout.creatorId}
                    </Link>
                  ) : (
                    layout.creatorName || '---'
                  )}
                </div>
                <LayoutDownloads layoutId={layout.id} layoutName={layout.name} />
              </div>

              <div className="actions">
                <AutoCompleteSelect
                  name={`clone-target-${layout.id}`}
                  label="Clone to organization"
                  value={cloneTargets[layout.id] || ''}
                  onChange={(_, value) => setCloneTargets((prev) => ({ ...prev, [layout.id]: value }))}
                  options={targetOptions}
                  required={false}
                />
                <OutlinedButton
                  disabled={!cloneTargets[layout.id] || cloningLayoutId === layout.id}
                  onClick={() => handleClone(layout.id)}
                >
                  {cloningLayoutId === layout.id ? 'Cloning...' : 'Clone layout'}
                </OutlinedButton>
              </div>
            </LayoutRow>
          ))
        ) : layoutsQuery.isError ? (
          <EmptyState className="flex items-center gap-3">
            <span>Failed to load layouts for this organization.</span>
            <TextButton type="button" onClick={() => void layoutsQuery.refetch()}>
              Retry
            </TextButton>
          </EmptyState>
        ) : isRefreshing ? (
          <EmptyState>Loading layouts...</EmptyState>
        ) : debouncedSearch ? (
          <EmptyState>No layouts match &quot;{debouncedSearch}&quot;.</EmptyState>
        ) : (
          <EmptyState>No layouts for this organization.</EmptyState>
        )}
      </LayoutsList>
    </OrgCard>
  );
};
